/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Checks if URL state has any data that needs label fetching
 * @param {Object} state - Current URL state
 * @param {Object} fieldToApiMapping - Mapping of URL field names to API field names
 * @returns {boolean} True if URL has data to fetch
 */
const hasUrlDataToFetch = (state, fieldToApiMapping) => {
  return Object.keys(fieldToApiMapping).some((fieldName) => {
    const values = state?.[fieldName];
    return values && Array.isArray(values) && values.length > 0;
  });
};

/**
 * Factory function for creating enriched data hooks
 * Follows the pattern established by useTreatmentEnrichedData
 *
 * @param {Object} config - Configuration for the enriched data hook
 * @param {Object} config.fieldToApiMapping - Maps URL field names to API request parameter names
 * @param {Object} config.apiToEnrichedMapping - Maps API response keys to enriched data keys
 * @param {Object} config.fieldToEnrichedMapping - Maps URL field names to enriched data keys
 * @param {Function} config.bulkInitialize - Async function to call the bulk API
 * @param {Object} config.initialEnrichedData - Initial state for enriched data
 * @param {string} config.hookName - Name for logging purposes
 * @returns {Function} A custom hook for managing enriched data
 */
export function createEnrichedDataHook(config) {
  const {
    fieldToApiMapping,
    apiToEnrichedMapping,
    fieldToEnrichedMapping,
    bulkInitialize,
    initialEnrichedData,
    hookName = 'useEnrichedData',
  } = config;

  return function useEnrichedData(urlState) {
    const [enrichedData, setEnrichedData] = useState(initialEnrichedData);
    const [isLoadingLabels, setIsLoadingLabels] = useState(() =>
      hasUrlDataToFetch(urlState, fieldToApiMapping)
    );
    const hasInitializedRef = useRef(false);

    /**
     * Transforms an API response item to a label object
     */
    const transformToLabelObject = useCallback((item) => {
      const label =
        item.french_name ||
        item.english_name ||
        item.latin_name ||
        item.name ||
        item.label ||
        item.group ||
        item.client_display_name ||
        item.display_name ||
        item.id;
      return {
        value: item.id,
        id: item.id,
        label,
      };
    }, []);

    /**
     * Bulk fetches labels for URL state restoration
     */
    const fetchLabelsForURLState = useCallback(
      async (state) => {
        const result = { ...initialEnrichedData };

        const bulkRequest = {};
        let hasAnyData = false;

        Object.entries(fieldToApiMapping).forEach(([fieldName, apiFieldName]) => {
          const values = state[fieldName];
          if (values && Array.isArray(values) && values.length > 0) {
            bulkRequest[apiFieldName] = values;
            hasAnyData = true;
          }
        });

        if (!hasAnyData) {
          return result;
        }

        try {
          const bulkData = await bulkInitialize(bulkRequest);

          Object.entries(apiToEnrichedMapping).forEach(([apiKey, enrichedKey]) => {
            if (bulkData[apiKey] && Array.isArray(bulkData[apiKey])) {
              result[enrichedKey] = bulkData[apiKey].map(transformToLabelObject);
            }
          });

          return result;
        } catch (error) {
          console.error(`[${hookName}] Failed to fetch labels:`, error);
          return result;
        }
      },
      [transformToLabelObject]
    );

    // Fetch labels ONLY on cold start (initial page load with IDs in URL)
    // Interactive filter selections handle labels via updateFilterWithLabels/setFilterLabel
    useEffect(() => {
      if (hasInitializedRef.current) {
        return;
      }

      const hasUrlData = hasUrlDataToFetch(urlState, fieldToApiMapping);

      if (!hasUrlData) {
        setIsLoadingLabels(false);
        hasInitializedRef.current = true;
        return;
      }

      hasInitializedRef.current = true;
      setIsLoadingLabels(true);

      fetchLabelsForURLState(urlState)
        .then((newEnrichedData) => {
          setEnrichedData(newEnrichedData);
          setIsLoadingLabels(false);
        })
        .catch((error) => {
          console.error(`[${hookName}] Error in useEffect:`, error);
          setIsLoadingLabels(false);
        });
    }, [urlState, fetchLabelsForURLState]);

    /**
     * Reset enriched data to initial state
     */
    const resetEnrichedData = useCallback(() => {
      setEnrichedData(initialEnrichedData);
      hasInitializedRef.current = false;
    }, []);

    /**
     * Update a specific field in enriched data
     */
    const updateEnrichedField = useCallback((field, items) => {
      const enrichedKey = fieldToEnrichedMapping[field];
      if (enrichedKey) {
        setEnrichedData((prev) => ({
          ...prev,
          [enrichedKey]: items,
        }));
      }
    }, []);

    /**
     * Get labels for a specific field
     */
    const getLabelsForField = useCallback(
      (field) => {
        const enrichedKey = fieldToEnrichedMapping[field];
        return enrichedKey ? enrichedData[enrichedKey] : [];
      },
      [enrichedData]
    );

    /**
     * Build selection labels mapping field names to arrays of label objects
     */
    const buildSelectionLabels = useCallback(() => {
      const selectionLabels = {};

      Object.entries(fieldToEnrichedMapping).forEach(([fieldName, enrichedKey]) => {
        const items = enrichedData[enrichedKey];
        if (items && items.length > 0) {
          selectionLabels[fieldName] = items;
        }
      });

      return selectionLabels;
    }, [enrichedData]);

    return {
      enrichedData,
      isLoadingLabels,
      resetEnrichedData,
      updateEnrichedField,
      getLabelsForField,
      buildSelectionLabels,
    };
  };
}
